<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
    <div class="modal-content" :style="modalContentStyle">
      <button class="close-button" @click="closeModal">&times;</button>
      <h2 class="modal-title">{{ exercise?.main_title }}</h2>
      <div class="video-container" :style="videoContainerStyle">
        <video
          v-if="exercise?.video_url"
          ref="videoPlayer"
          :src="exercise.video_url"
          controls
          autoplay
          class="modal-video"
          :style="modalVideoStyle"
          @play="onVideoPlay"
          @timeupdate="onTimeUpdate"
          @ended="onVideoEnded"
          @pause="onVideoPause"
        ></video>
      </div>
    </div>
  </div>
</template>

<script>
import Hls from 'hls.js'
import { trackVideoView } from '@/services/firebase'
import { useAILayout } from '@/composables/useAILayout'

export default {
  name: 'VideoModal',
  props: {
    exercise: {
      type: Object,
      default: null
    },
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup() {
    const { containerStyle, gridStyle, layoutConfig } = useAILayout()
    return { containerStyle, gridStyle, layoutConfig }
  },
  computed: {
    modalContentStyle() {
      return {
        background: '#14171F',
        borderRadius: '12px',
        padding: this.layoutConfig.padding,
        width: this.layoutConfig.gridWidth,
        maxWidth: '1200px',
        maxHeight: '90vh',
        position: 'relative',
        overflow: 'hidden'
      }
    },
    videoContainerStyle() {
      return {
        position: 'relative',
        width: '100%',
        height: this.layoutConfig.gridHeight
      }
    },
    modalVideoStyle() {
      return {
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        borderRadius: '8px',
        display: 'block',
        objectFit: 'contain'
      }
    }
  },
  data() {
    return {
      watchDuration: 0,
      lastTrackedTime: 0,
      isPlaying: false,
      videoDuration: 0,
      hls: null
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initVideo()
        })
      } else {
        this.destroyHls()
      }
    }
  },
  methods: {
    initVideo() {
      const video = this.$refs.videoPlayer
      if (!video || !this.exercise?.video_url) return

      const videoUrl = this.exercise.video_url

      // Check if it's an HLS stream
      if (videoUrl.includes('.m3u8')) {
        if (Hls.isSupported()) {
          this.hls = new Hls()
          this.hls.loadSource(videoUrl)
          this.hls.attachMedia(video)
          this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play()
          })
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Native HLS support (Safari)
          video.src = videoUrl
          video.play()
        }
      } else {
        // Regular video file
        video.src = videoUrl
        video.play()
      }
    },

    destroyHls() {
      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }
    },

    onVideoPlay() {
      this.isPlaying = true
      if (this.$refs.videoPlayer) {
        this.videoDuration = this.$refs.videoPlayer.duration
      }
    },
    
    onVideoPause() {
      this.isPlaying = false
    },
    
    onTimeUpdate() {
      if (!this.$refs.videoPlayer) return
      
      const currentTime = this.$refs.videoPlayer.currentTime
      const duration = this.$refs.videoPlayer.duration
      
      // Track watch duration (every 3 seconds = 1 view)
      if (currentTime - this.lastTrackedTime >= 3) {
        this.watchDuration += 3
        this.lastTrackedTime = currentTime
        
        // Track view every 3 seconds
        console.log('Tracking view at', this.watchDuration, 'seconds')
        this.trackView(false)
      }
      
      // Store duration for full view check
      this.videoDuration = duration
    },
    
    async onVideoEnded() {
      this.isPlaying = false
      
      // Track full view when video completes
      await this.trackView(true)
      
      // Navigate to gift page
      this.$router.push('/gift')
    },
    
    async trackView(isFullView) {
      try {
        await trackVideoView(
          this.exercise?.id || 'unknown',
          this.exercise?.main_title || 'Unknown Video',
          this.watchDuration,
          isFullView
        )
      } catch (error) {
        console.error('Failed to track video view:', error)
      }
    },
    
    closeModal() {
      // Track partial view if video was watched but not completed
      if (this.watchDuration > 0 && this.isPlaying) {
        this.trackView(false)
      }
      
      this.watchDuration = 0
      this.lastTrackedTime = 0
      this.isPlaying = false
      
      this.$emit('close')
    }
  },
  beforeUnmount() {
    this.destroyHls()
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #14171F;
  border-radius: 12px;
  padding: 20px;
  width: 90%;
  max-width: 1200px;
  max-height: 90%;
  position: relative;
  overflow: hidden;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  color: #fff;
  font-size: 32px;
  cursor: pointer;
  z-index: 10;
  line-height: 1;
}

.close-button:hover {
  color: #ccc;
}

.modal-title {
  color: #fff;
  margin: 0 0 15px 0;
  font-size: 1.5rem;
  padding-right: 40px;
}

.video-container {
  position: relative;
}

.modal-video {
  width: 100%;
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
  display: block;
}
</style>
